/**
 * useAnnotations — comparison-level annotation state hook (M4 Phase 4.4).
 *
 * Owns the cache of `AnnotationResponse` rows keyed by `changeId`, plus
 * the optimistic / persisted update flow used by AnnotationThread.
 *
 * Mirrors the structure of `useDecisions`: a single fetch on mount
 * primes the cache, mutations apply optimistically + reconcile against
 * the server response, and `refresh()` re-fetches the comparison-level
 * list (used after role flips so cross-tab edits show up).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  veridocApi,
  type AnnotationResponse,
  type AnnotationVisibility,
} from '../../lib/veridoc-api';
import { ApiError } from '../../lib/transport';

interface AnnotationsByChange {
  [changeId: string]: AnnotationResponse[];
}

export interface UseAnnotationsOptions {
  comparisonId: string | undefined;
  /** Roles other than viewer can author. */
  canAuthor: boolean;
  /** Current user id — used to gate edit / delete UI on the client. */
  currentUserId: string | undefined;
  /** Toast / banner emitter (any string surfaces verbatim). */
  onError?: (message: string) => void;
}

export interface UseAnnotationsResult {
  /** Snapshot of annotations for one change (always returns array). */
  list(changeId: string): AnnotationResponse[];
  /** Total count for one change — uses the cache, not the wire field. */
  count(changeId: string): number;
  /** True while a mutation for the given change is in flight. */
  isPending(changeId: string): boolean;
  create(
    changeId: string,
    body: string,
    visibility?: AnnotationVisibility,
  ): Promise<AnnotationResponse | null>;
  update(
    annotationId: string,
    body: string,
  ): Promise<AnnotationResponse | null>;
  remove(annotationId: string): Promise<boolean>;
  /** Re-fetch the entire comparison annotation list. */
  refresh(): Promise<void>;
  canAuthor: boolean;
  currentUserId: string | undefined;
}

export function useAnnotations({
  comparisonId,
  canAuthor,
  currentUserId,
  onError,
}: UseAnnotationsOptions): UseAnnotationsResult {
  const [byChange, setByChange] = useState<AnnotationsByChange>({});
  const pendingRef = useRef<Set<string>>(new Set());
  const [pendingTick, setPendingTick] = useState<number>(0);

  const setPending = useCallback((changeId: string, pending: boolean) => {
    if (pending) {
      pendingRef.current.add(changeId);
    } else {
      pendingRef.current.delete(changeId);
    }
    setPendingTick((tick) => tick + 1);
  }, []);

  const isPending = useCallback(
    (changeId: string): boolean => pendingRef.current.has(changeId),
    // pendingTick triggers re-render; ref read is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingTick],
  );

  // onError is captured in a ref so that `refresh`'s identity is stable across
  // re-renders. Inline arrows at the call site (e.g. the toast wrapper in
  // ComparisonWorkspace.tsx) get a fresh identity on every render; if `onError`
  // were in `refresh`'s dep list, every successful fetch would re-render the
  // host, churn the inline arrow, recreate `refresh`, and re-fire the mount
  // effect — a tight request loop that blew past the 60/min IP rate limiter
  // and surfaced as a 429 flood on /comparisons/{id}/annotations.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  const refresh = useCallback(async () => {
    if (!comparisonId) return;
    try {
      const result = await veridocApi.listComparisonAnnotations(comparisonId);
      const grouped: AnnotationsByChange = {};
      for (const annotation of result.annotations) {
        const list = grouped[annotation.changeId] ?? [];
        list.push(annotation);
        grouped[annotation.changeId] = list;
      }
      setByChange(grouped);
    } catch (err) {
      const handler = onErrorRef.current;
      if (handler) handler(humanizeError(err));
    }
  }, [comparisonId]);

  // Prime the cache once on mount + whenever the comparison changes.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const list = useCallback(
    (changeId: string): AnnotationResponse[] => byChange[changeId] ?? [],
    [byChange],
  );

  const count = useCallback(
    (changeId: string): number => list(changeId).length,
    [list],
  );

  const create = useCallback(
    async (
      changeId: string,
      body: string,
      visibility?: AnnotationVisibility,
    ): Promise<AnnotationResponse | null> => {
      const trimmed = body.trim();
      if (!trimmed) return null;

      setPending(changeId, true);
      try {
        const created = await veridocApi.createAnnotation(
          changeId,
          trimmed,
          visibility ?? 'private',
        );
        setByChange((prev) => {
          const existing = prev[changeId] ?? [];
          return { ...prev, [changeId]: [...existing, created] };
        });
        return created;
      } catch (err) {
        if (onError) onError(humanizeError(err));
        return null;
      } finally {
        setPending(changeId, false);
      }
    },
    [onError, setPending],
  );

  const update = useCallback(
    async (
      annotationId: string,
      body: string,
    ): Promise<AnnotationResponse | null> => {
      const trimmed = body.trim();
      if (!trimmed) return null;

      const changeId = findChangeIdForAnnotation(byChange, annotationId);
      if (changeId) setPending(changeId, true);
      try {
        const updated = await veridocApi.updateAnnotation(annotationId, trimmed);
        setByChange((prev) => {
          const list = prev[updated.changeId] ?? [];
          const next = list.map((a) => (a.id === updated.id ? updated : a));
          return { ...prev, [updated.changeId]: next };
        });
        return updated;
      } catch (err) {
        if (onError) onError(humanizeError(err));
        return null;
      } finally {
        if (changeId) setPending(changeId, false);
      }
    },
    [byChange, onError, setPending],
  );

  const remove = useCallback(
    async (annotationId: string): Promise<boolean> => {
      const changeId = findChangeIdForAnnotation(byChange, annotationId);
      if (changeId) setPending(changeId, true);
      try {
        await veridocApi.deleteAnnotation(annotationId);
        setByChange((prev) => {
          if (!changeId) return prev;
          const list = prev[changeId] ?? [];
          return {
            ...prev,
            [changeId]: list.filter((a) => a.id !== annotationId),
          };
        });
        return true;
      } catch (err) {
        if (onError) onError(humanizeError(err));
        return false;
      } finally {
        if (changeId) setPending(changeId, false);
      }
    },
    [byChange, onError, setPending],
  );

  return useMemo(
    () => ({
      list,
      count,
      isPending,
      create,
      update,
      remove,
      refresh,
      canAuthor,
      currentUserId,
    }),
    [
      list,
      count,
      isPending,
      create,
      update,
      remove,
      refresh,
      canAuthor,
      currentUserId,
    ],
  );
}

function findChangeIdForAnnotation(
  byChange: AnnotationsByChange,
  annotationId: string,
): string | undefined {
  for (const [changeId, list] of Object.entries(byChange)) {
    if (list.some((a) => a.id === annotationId)) return changeId;
  }
  return undefined;
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return err.code === 'VD_ANNOTATION_NOT_AUTHOR'
        ? '只有标注作者可以编辑或删除'
        : '当前角色无写入权限';
    }
    if (err.status === 404) return '该标注或变更已被删除';
    if (err.status === 409) return '编辑窗口已关闭（标注 5 分钟内可编辑）';
    if (err.status === 422) {
      if (err.code === 'VD_ANNOTATION_VISIBILITY_IMMUTABLE') {
        return '可见性创建后不可修改';
      }
      if (err.code === 'VD_ANNOTATION_INVALID_VISIBILITY') {
        return '可见性取值非法';
      }
      return err.message || '内容长度不合规（1-5000 字）';
    }
    return err.message || `操作失败 (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : String(err);
}

// Re-export for AnnotationThread to use the same humanizer.
export { humanizeError as humanizeAnnotationError };
